import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormRadio from '@ui/form/input/FormRadio';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import Legend from '@ui/legend/Legend';
import RankLineChart, { RankLineSeries } from '@ui/rank/RankLineChart';
import RankLineSwatch from '@ui/rank/RankLineSwatch';
import { RANK_EMPHASIS_COLOR, rankLineStyle } from '@ui/rank/rankScale';
import { BookingPositionState } from './model/BookingPositionState';
import {
	cellGapLabel,
	cellRankValue,
	chartWindowText,
	dayLabel,
	mainHotel,
	NOT_IN_500,
	noPriceCheckNights,
	positionText,
	rankValue,
	sameCityHotels,
} from './BookingRanking.utilities';
import { RankingCard, RankingCardHead } from './BookingRankingParts';

type ChartView = 'EACH' | 'MIDDLE';

// The chart card of the simple view: the switch "Each competitor" | "Compset middle" (D18), the position
// chart, the legend with one tick box per competitor (untick = hide the line), the key and the foot note.
const PositionChartCard: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const form = useForm<{ view: ChartView }>({ defaultValues: { view: 'EACH' } });
	const view = form.watch('view');
	const hotels = sameCityHotels(state);
	const main = mainHotel(state);
	const competitors = hotels.filter((h) => !h.isMain);
	// Each view keeps its own ticks: all lines in "Each competitor", no line in "Compset middle".
	const [hiddenInEach, setHiddenInEach] = useState<number[]>([]);
	const [shownInMiddle, setShownInMiddle] = useState<number[]>([]);
	const isShown = (hotelId: number) => (view === 'EACH' ? !hiddenInEach.includes(hotelId) : shownInMiddle.includes(hotelId));
	const toggle = (hotelId: number) => {
		if (view === 'EACH') setHiddenInEach((ids) => (ids.includes(hotelId) ? ids.filter((id) => id !== hotelId) : [...ids, hotelId]));
		else setShownInMiddle((ids) => (ids.includes(hotelId) ? ids.filter((id) => id !== hotelId) : [...ids, hotelId]));
	};

	const city = state.bookingCity.name;
	const scan = state.scan;
	const dates = useMemo(() => state.nights.map((n) => n.date), [state.nights]);
	// The own hotel is the line in front (blue-600). A competitor takes the colour of its place in the table
	// (rankLineStyle(index)), so a hidden line does not change the colours of the others.
	const series: RankLineSeries[] = [
		...competitors.flatMap((h, index) => (isShown(h.hotelId)
			? [{ id: String(h.hotelId), label: h.name, values: h.cells.map(cellRankValue), gapLabels: h.cells.map(cellGapLabel), ...rankLineStyle(index) }]
			: [])),
		...(main === undefined ? [] : [{ id: String(main.hotelId), label: main.name, emphasis: true, color: RANK_EMPHASIS_COLOR, values: main.cells.map(cellRankValue), gapLabels: main.cells.map(cellGapLabel) }]),
	];
	const isMiddle = view === 'MIDDLE';
	const noCheck = noPriceCheckNights(state);
	const lead = isMiddle ? 'You and the middle of your compset' : 'Each competitor';

	return <RankingCard>
		<RankingCardHead
			title={`Position in ${city} by stay night`}
			subtitle={`${lead} · every stay night of the next 6 months · top = position 1${scan !== null ? ` · ${chartWindowText(scan, state.months)}` : ''}`}
			right={<>
				<span role="radiogroup" aria-label="Chart view" className="inline-flex items-center">
					<FormRadio form={form} name="view" options={[{ label: 'Each competitor', value: 'EACH' }, { label: 'Compset middle', value: 'MIDDLE' }]} />
				</span>
			</>} />
		<div className="px-4 pb-1.5 pt-3.5 sm:px-5">
			<RankLineChart
				ariaLabel={`Position in ${city} by stay night, ${dates.length} nights`}
				dates={dates}
				series={series}
				reference={isMiddle ? {
					label: 'Middle of your compset',
					values: state.nights.map((n) => rankValue(n.compsetMiddle, n.compsetMiddleAfter500)),
				} : undefined}
				band={isMiddle ? {
					label: 'Middle half of your compset',
					lo: state.nights.map((n) => rankValue(n.compsetBandLo, n.compsetBandLoAfter500)),
					hi: state.nights.map((n) => rankValue(n.compsetBandHi, n.compsetBandHiAfter500)),
				} : undefined}
				markers={main === undefined ? undefined : { label: 'Sold out', flags: main.cells.map((c) => c.state === 'SOLD_OUT') }}
				overLabel="500+"
				formatValue={(value) => (value === null ? 'no position' : (value === 'over' ? NOT_IN_500.toLowerCase() : String(value)))}
				scrollHint="Swipe the chart to see all 6 months." />
		</div>
		<div className="flex flex-col gap-2.5 px-4 pb-4 pt-2 sm:px-5">
			<div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-600">
				{main !== undefined && <span className="inline-flex items-center gap-1.5">
					<RankLineSwatch emphasis />
					<span><span className="font-semibold text-blue-700">{main.name}</span> · typical {positionText(main.typical, main.typicalAfter500)}</span>
				</span>}
				{isMiddle && <>
					<span className="inline-flex items-center gap-1.5">
						<RankLineSwatch reference />Middle of your compset ({competitors.length} hotels in {city})
					</span>
					<span className="inline-flex items-center gap-1.5">
						<span aria-hidden="true" className="inline-block h-2.5 w-3.5 shrink-0 rounded-sm bg-slate-300" />Middle half of your compset
					</span>
				</>}
			</div>
			<p className="text-xs font-semibold text-gray-700">
				{isMiddle ? 'Show a competitor\'s line' : 'Competitors on the chart (untick to hide a line)'}
			</p>
			<ul className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
				{competitors.map((hotel, index) => <li key={hotel.hotelId} className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
					<RankLineSwatch index={index} />
					<span className="min-w-0 flex-1">
						<FormCheckbox name={`line-${hotel.hotelId}`} label={hotel.name} checked={isShown(hotel.hotelId)}
							onChange={() => toggle(hotel.hotelId)} labelExtraClassName="text-sm font-normal text-gray-700" />
					</span>
					<span className="whitespace-nowrap text-xs text-gray-600">Typical {positionText(hotel.typical, hotel.typicalAfter500)}</span>
				</li>)}
			</ul>
			<Legend groups={[{
				label: 'Key',
				items: [
					{ label: '500+ = not in the top 500', mark: 'square', markClassName: 'bg-gray-100 ring-1 ring-inset ring-gray-300' },
					{ label: 'You: sold out', mark: 'square', markClassName: 'bg-blue-900' },
					{ label: 'no position; the tooltip says why (sold out, not found, no data)', icon: <span className="text-xs font-semibold text-gray-700">Gap</span> },
				],
			}]} />
			<p className="text-xs leading-5 text-gray-600">
				{scan?.window === 'LAST_7_DAYS'
					? 'Each point is the middle position of the checks of the night in the last 7 days. '
					: `Each point is the middle position of the ${scan?.checksPerNight ?? 3} checks of the night in the first scan. `}
				Typical = the middle position of all checks of the 6 months.
				{scan?.window !== 'LAST_7_DAYS' && noCheck.length > 0 && ` ${noCheck.map(dayLabel).join(', ')}: RoomPulse has no Booking.com price check of `
					+ `${noCheck.length === 1 ? 'this night' : 'these nights'}, so a hotel that no check finds shows a gap (no data), not a place.`}
				<span className="hidden sm:inline"> Point at a night to see every value.</span>
			</p>
		</div>
	</RankingCard>;
};

export default PositionChartCard;
